import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Download, Share2, Copy, ExternalLink, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { campaignDisplayLabel } from "./Execution";

export default function Reports() {
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState([]);
  const [cities, setCities] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/snapshots");
      setSnapshots(r.data);
    } catch {}
  };

  useEffect(() => {
    refresh();
    api.get("/masters/cities").then((r) => setCities(r.data));
    api.get("/campaigns", { params: { limit: 500 } }).then((r) => {
      setCampaigns(r.data);
      if (r.data[0] && !selectedCampaign) setSelectedCampaign(r.data[0].campaign_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCSV = async (path, filename) => {
    try {
      const token = localStorage.getItem("bwz_token");
      const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const removeSnap = async (id) => {
    if (!confirm("Revoke this share link?")) return;
    try { await api.delete(`/snapshots/${id}`); toast.success("Revoked"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/snapshot/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const canShare = user && ["admin", "leadership", "command_center"].includes(user.role);

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Reports & Exports</p>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Reports</h2>
      </div>

      <Card className="p-5" data-testid="csv-exports-card">
        <h3 className="font-display text-lg font-semibold mb-1">CSV exports</h3>
        <p className="text-xs text-zinc-500 mb-4">Download point-in-time snapshots of campaigns and execution updates.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={() => downloadCSV("/reports/campaigns.csv", "campaigns.csv")} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="export-campaigns-csv">
            <Download className="h-4 w-4 mr-2" /> Campaigns CSV
          </Button>
          <Button onClick={() => downloadCSV("/reports/executions.csv", "executions.csv")} variant="outline" data-testid="export-executions-csv">
            <Download className="h-4 w-4 mr-2" /> Executions CSV
          </Button>
        </div>
      </Card>

      <Card className="p-5" data-testid="campaign-report-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Campaign-wise daily performance
            </h3>
            <p className="text-xs text-zinc-500 mb-3">
              Day-by-day breakdown of planned vs completed (T / N.T / total) for a single campaign — includes the campaign header (client · city · pod · target · period) and a TOTAL row.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1 min-w-[260px]">
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Select campaign</Label>
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                  <SelectTrigger data-testid="campaign-report-select"><SelectValue placeholder="Choose a campaign…" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => (
                      <SelectItem key={c.campaign_id} value={c.campaign_id}>
                        {campaignDisplayLabel(c)}
                        <span className="text-zinc-400 ml-2">· {c.campaign_id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => {
                  if (!selectedCampaign) { toast.error("Pick a campaign first"); return; }
                  downloadCSV(`/reports/campaign/${selectedCampaign}.csv`, `campaign-${selectedCampaign}-daily.csv`);
                }}
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
                data-testid="download-campaign-report"
              >
                <Download className="h-4 w-4 mr-2" /> Download report
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {canShare && (
        <Card className="overflow-hidden" data-testid="snapshots-card">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold">Public share links</h3>
              <p className="text-xs text-zinc-500">Public, read-only campaign snapshots — drop into WhatsApp / email. View counts tracked.</p>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="new-snapshot-button">
                  <Share2 className="h-4 w-4 mr-1.5" /> New share link
                </Button>
              </DialogTrigger>
              <NewSnapshotDialog cities={cities} onClose={() => setOpen(false)} onCreated={refresh} />
            </Dialog>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
              <tr>
                <th className="text-left py-2.5 px-4 font-semibold">Title</th>
                <th className="text-left py-2.5 px-4 font-semibold">Scope</th>
                <th className="text-left py-2.5 px-4 font-semibold">Created</th>
                <th className="text-left py-2.5 px-4 font-semibold">Expires</th>
                <th className="text-left py-2.5 px-4 font-semibold">Views</th>
                <th className="text-left py-2.5 px-4 font-semibold">Link</th>
                <th className="text-left py-2.5 px-4 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody>
              {snapshots.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-xs">No share links yet.</td></tr>}
              {snapshots.map((s) => (
                <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`snapshot-row-${s.id}`}>
                  <td className="py-2.5 px-4 font-medium">{s.title}</td>
                  <td className="py-2.5 px-4 capitalize text-zinc-600">{s.scope_type}{s.scope_value ? ` · ${s.scope_value}` : ""}</td>
                  <td className="py-2.5 px-4 font-mono text-xs text-zinc-500">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="py-2.5 px-4 font-mono text-xs text-zinc-500">{(s.expires_at || "").slice(0, 10)}</td>
                  <td className="py-2.5 px-4 font-mono text-xs">{s.view_count}</td>
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => copyLink(s.token)} className="text-xs text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1" data-testid={`copy-link-${s.id}`}>
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                      <a href={`/snapshot/${s.token}`} target="_blank" rel="noreferrer" className="text-xs text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1" data-testid={`open-link-${s.id}`}>
                        <ExternalLink className="h-3 w-3" /> Open
                      </a>
                    </div>
                  </td>
                  <td className="py-2.5 px-4">
                    <button onClick={() => removeSnap(s.id)} className="text-zinc-400 hover:text-red-600" data-testid={`revoke-snap-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function NewSnapshotDialog({ cities, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "Q1 progress snapshot", scope_type: "all", scope_value: "", expires_in_days: 14 });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, expires_in_days: Number(form.expires_in_days) };
      if (payload.scope_type === "all") payload.scope_value = null;
      const { data } = await api.post("/snapshots", payload);
      toast.success("Share link created");
      onCreated(); onClose();
      navigator.clipboard.writeText(`${window.location.origin}/snapshot/${data.token}`);
      toast.message("Link copied to clipboard");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };
  return (
    <DialogContent data-testid="new-snapshot-dialog">
      <DialogHeader><DialogTitle className="font-display">Create public snapshot</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="snap-title" />
        </div>
        <div>
          <Label className="text-xs">Scope</Label>
          <Select value={form.scope_type} onValueChange={(v) => setForm({ ...form, scope_type: v, scope_value: "" })}>
            <SelectTrigger data-testid="snap-scope-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              <SelectItem value="city">By city</SelectItem>
              <SelectItem value="zone">By zone</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.scope_type === "city" && (
          <div>
            <Label className="text-xs">City</Label>
            <Select value={form.scope_value} onValueChange={(v) => setForm({ ...form, scope_value: v })}>
              <SelectTrigger data-testid="snap-scope-city"><SelectValue placeholder="Pick a city" /></SelectTrigger>
              <SelectContent>
                {cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {form.scope_type === "zone" && (
          <div>
            <Label className="text-xs">Zone</Label>
            <Input value={form.scope_value} onChange={(e) => setForm({ ...form, scope_value: e.target.value })} placeholder="South / North / …" data-testid="snap-scope-zone" />
          </div>
        )}
        <div>
          <Label className="text-xs">Expires in (days)</Label>
          <Input type="number" min={1} max={90} value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} data-testid="snap-expires" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="snap-cancel">Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="snap-submit">
          {submitting ? "Creating…" : "Create & copy link"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
